import React, { useContext, useEffect, useState } from "react";
import { Text, Title } from "rizzui/typography";
import { timeOptions, timezones } from "../lib/constants";
import { Controller, useForm } from "react-hook-form";
import { Switch } from "rizzui/switch";
import { useColorStyle } from "../hooks/useColorStyle";
import { ThemeContext } from "../context/themeContext";
import { labelClassName } from "../modules/projects/new/first";
import cn from "../lib/utils/class-name";
import Select from "react-select";
import PlusIcon from "../assets/plus";
import { Button } from "rizzui/button";
import { useModal } from "../hooks/useModal";
import MinusIcon from "../assets/minus";
import {
  convertTo12Hour,
  convertTo24Hour,
  getFullDayName,
} from "../lib/utils/convertTo24hrs";
import axios from "axios";
import { baseUrl } from "../modules/account/login";
import { AuthContext } from "../context/authContext";
import { useNavigate } from "react-router";
import { useCampaigns, useSingleAvailability } from "../hooks/useCampaign";
import { useNotifcication } from "../hooks/useNotification";
import BinIcon from "../assets/bin";
import { useQueryClient } from "@tanstack/react-query";
import { start } from "repl";

export default function SetAvailability({
  page,
  id,
}: {
  page: "appointments" | "campaign";
  id?: string;
}) {
  const { theme } = useContext(ThemeContext);
  const { view, closeModal } = useModal();
  const [dayError, setDayError] = useState("");
  const [currentId, setCurrentId] = useState(id);
  const [loading, setLoading] = useState(false);
  const [currentTimeZone, setSetCurrentTimeZone] = useState<any>();

  const { timeColorStyle, campaignColorStyles } = useColorStyle(theme);
  const { openNotification } = useNotifcication();

  const {
    control,
    watch,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    getValues,
  } = useForm();

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();
  const { data, isLoading } = useCampaigns();
  const campaigns = data?.map((campaign) => ({
    value: campaign._id,
    label: campaign.campaign_name,
  }));

  const queryClient = useQueryClient();

  interface TimeSlot {
    startTime: string;
    endTime: string;
  }
  const { data: availability } = useSingleAvailability({
    id: currentId,
  });

  useEffect(() => {
    // Group backend data into days with times
    const groupedAvailability: { [key: string]: TimeSlot[] } = {};

    availability?.data?.forEach((item) => {
      if (!groupedAvailability[item.day]) {
        groupedAvailability[item.day] = [];
      }
      groupedAvailability[item.day].push({
        startTime: item.start_time,
        endTime: item.end_time,
      });
    });

    // Convert to array
    const formattedAvailability = Object.keys(groupedAvailability).map(
      (day) => ({
        day,
        times: groupedAvailability[day],
      })
    );

    days.forEach((day) => {
      const dayAvailability = formattedAvailability.find(
        (availability) => availability.day === getFullDayName(day)
      );
      if (dayAvailability) {
        setValue(`callDays.${day}.active`, true);
        setValue(
          `callDays.${day}.time`,
          dayAvailability.times.map((time) => ({
            start: "",
            end: "",
            id: Date.now() + Math.random(),
          }))
        );
        dayAvailability.times.forEach((time: TimeSlot, index: number) => {
          setValue(
            `callDays.${day}.time.${index}.start`,
            timeOptions.find(
              (option) => option.value === convertTo12Hour(time.startTime)
            )
          );
          setValue(
            `callDays.${day}.time.${index}.end`,
            timeOptions.find(
              (option) => option.value === convertTo12Hour(time.endTime)
            )
          );
        });
      } else {
        setValue(`callDays.${day}.active`, false);
        setValue(`callDays.${day}.time`, []);
      }
    });
  }, [availability, view]);

  const SetAvailability = () => {
    const callDays = getValues("callDays");
    let availability = Object.keys(callDays).map((day) => {
      if (callDays[day]?.active) {
        return {
          day: getFullDayName(day),
          slots: callDays[day]?.time.map((time) => ({
            start: convertTo24Hour(time?.start?.value),
            end: convertTo24Hour(time?.end?.value),
          })),
        };
      } else return false;
    });
    availability = availability.filter((value) => value !== false);
    setLoading(true);
    return axios
      .post(
        `${baseUrl}/appointments/set_availability`,
        { campaign_id: id, availability },
        { headers: { Authorization: AuthStr } }
      )
      .then((response) => {
        if (response.data?.success) {
          setLoading(false);
          queryClient.invalidateQueries({
            queryKey: [`${currentId}_campaign_availability`, token],
          });
          closeModal();
          openNotification({ status: "success", message: "Success" });
        }
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };

  const handleAvailability = handleSubmit((data) => {
    const callday = getValues("callDays");
    if (
      !callday?.Mon?.active &&
      !callday?.Tue?.active &&
      !callday?.Wed?.active &&
      !callday?.Thu?.active &&
      !callday?.Fri?.active &&
      !callday?.Sat?.active &&
      !callday?.Sun?.active
    ) {
      setDayError("Select at least one day");
    } else {
      setDayError("");
      //refetch();
      // console.log(response, callError);
      //setDone(true);
      SetAvailability();
    }
  });

  useEffect(() => {
    setCurrentId(id);
    setValue(
      "campaign",
      campaigns?.find((campaign) => id === campaign?.value)
    );
    setSetCurrentTimeZone(null);
    setValue("timezone", null);
    setDayError("");
  }, [view]);

  return (
    <div className="customBar overflow-y-auto mx-auto max-h-[715px] min-h-[342px] h-auto customBar bg-background dark:bg-[#2A2C30] border-muted md:w-[471px] w-[94%] rounded-[8px] border-[0.5px]">
      <button
        className="absolute top-4 right-4 p-1 rounded-lg z-10 text-gray-400 bg-transparent hover:text-gray-600 dark:hover:text-gray-300"
        onClick={closeModal}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="size-[18px]"
        >
          <path
            fillRule="evenodd"
            d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleAvailability();
        }}
        noValidate
        className="flex flex-col gap-[28px] p-[32px]"
      >
        <div className="flex flex-col gap-[8px] ">
          <Title className="font-ibmPlexSerif font-[600] text-[18px] text-primary-dark">
            {availability?.data?.length > 0 ? "Adjust " : "Set "} your
            availablity
          </Title>
          {page === "campaign" && (
            <Text className="max-w-[379px] font-switzer text-[14px] font-[400] text-[#758599]">
              Setting your availability will make it easy for RingConnect to set
              multiple appointments without a clash
            </Text>
          )}
        </div>

        <div className="flex flex-col gap-[16px] z-50">
          {page === "appointments" && (
            <Controller
              control={control}
              name="campaign"
              rules={{
                required: {
                  value: true,
                  message: "Required",
                },
              }}
              render={({ field: { onChange, value, name } }) => (
                <div className="flex flex-col gap-[8px]">
                  <Text className={labelClassName}>Campaign</Text>
                  <Select
                    options={campaigns}
                    placeholder=""
                    styles={campaignColorStyles}
                    isSearchable={true}
                    value={campaigns.find(
                      (campaign) => currentId === campaign?.value
                    )}
                    name={name}
                    isLoading={isLoading}
                    onChange={(e) => {
                      setCurrentId(e.value);
                      onChange(e);
                    }}
                  />
                  {errors.campaign?.message && (
                    <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                      {"" + errors.campaign.message}
                    </p>
                  )}
                </div>
              )}
            />
          )}

          <Controller
            control={control}
            name="timezone"
            rules={{
              required: {
                value: true,
                message: "Required",
              },
            }}
            render={({ field: { onChange, value, name } }) => (
              <div className="flex flex-col gap-[8px]">
                <Text className={labelClassName}>Timezone</Text>
                <Select
                  options={timezones}
                  placeholder="Select an option"
                  styles={campaignColorStyles}
                  isSearchable={true}
                  value={
                    timezones.find(
                      (timezone) => currentTimeZone === timezone?.value
                    ) ?? null
                  }
                  name={name}
                  onChange={(e) => {
                    setSetCurrentTimeZone(e?.value);
                    onChange(e?.value);
                  }}
                />
                {errors.timezone?.message && (
                  <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                    {"" + errors.timezone.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        <div className="flex flex-col gap-[8px] font-switzer text-[14px] font-[400] text-[#758599]">
          <Text className="font-switzer text-[12px] font-[400] text-[#758599]">
            Available Day and Time
          </Text>

          <div className="border border-dashed border-[#758599] max-h-[400px] flex flex-col gap-[8px] px-[16px] py-[13px] overflow-y-auto customBar">
            <div className="flex flex-col h-[95%] w-[95%]">
              {days.map((day, i) => {
                const active = watch(`callDays.${day}.active`);
                let time = watch(`callDays.${day}.time`);
                return (
                  <div
                    className="flex flex-row md:gap-[16px] gap-[4px] justify-between min-h-[32px] h-fit place-items-start"
                    key={i}
                  >
                    <Controller
                      control={control}
                      name={`callDays.${day}.active`}
                      render={({ field: { onChange, value, name } }) => (
                        <div className="w-[120px] overflow-hidden">
                          <Switch
                            variant="flat"
                            label={day}
                            size="lg"
                            checked={value}
                            value={value}
                            name={name}
                            onChange={(e) => {
                              e && setDayError("");
                              e
                                ? setValue(`callDays.${day}.time`, [
                                    {
                                      start: "",
                                      end: "",
                                      id: Date.now() + Math.random(),
                                    },
                                  ])
                                : setValue(`callDays.${day}.time`, []);
                              onChange(e);
                            }}
                            labelClassName={cn(labelClassName, "ml-[19px]")}
                          />
                        </div>
                      )}
                    />
                    <div className="flex flex-col gap-[8px] w-[calc(100%-100px)] place-content-start">
                      {active &&
                        time?.map((slot, index) => {
                          const startError =
                            errors?.callDays?.[day]?.time?.[index]?.start;
                          const endError =
                            errors?.callDays?.[day]?.time?.[index]?.end;
                          return (
                            <div className="flex justify-between w-full place-items-center">
                              <div
                                className="w-[216px] flex justify-between"
                                key={slot.id}
                              >
                                <Controller
                                  control={control}
                                  name={`callDays.${day}.time.${index}.start`}
                                  rules={{
                                    required: {
                                      value: active ? true : false,
                                      message: "Required",
                                    },
                                  }}
                                  render={({
                                    field: { onChange, value, name },
                                  }) => {
                                    return (
                                      <Select
                                        options={timeOptions}
                                        placeholder="Start time"
                                        styles={{
                                          ...timeColorStyle,
                                          menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                          }),
                                        }}
                                        menuPortalTarget={document.body}
                                        isSearchable={false}
                                        isDisabled={!active}
                                        className={cn(
                                          "w-[96px] h-[32px]",
                                          startError
                                            ? "[&>div]:!border-red"
                                            : ""
                                        )}
                                        value={value}
                                        name={name}
                                        onChange={(e) => {
                                          const updatedTime = [...time];
                                          updatedTime[index] = {
                                            ...updatedTime[index],
                                            start: e.value,
                                          };
                                          setValue(
                                            `callDays.${day}.time`,
                                            updatedTime
                                          );
                                          onChange(e);
                                        }}
                                      />
                                    );
                                  }}
                                />
                                <Controller
                                  control={control}
                                  name={`callDays.${day}.time.${index}.end`}
                                  rules={{ required: true }}
                                  render={({
                                    field: { onChange, value, name },
                                  }) => {
                                    const startTimeIndex =
                                      timeOptions.findIndex(
                                        (option) =>
                                          option.value ===
                                          (time[index]?.start?.value || "")
                                      );
                                    return (
                                      <Select
                                        options={timeOptions.slice(
                                          startTimeIndex + 1 || 0
                                        )}
                                        placeholder="End time"
                                        styles={{
                                          ...timeColorStyle,
                                          menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                          }),
                                        }}
                                        menuPortalTarget={document.body}
                                        isSearchable={false}
                                        isDisabled={!active}
                                        className={cn(
                                          "w-[96px] h-[32px]",
                                          endError ? "[&>div]:!border-red" : ""
                                        )}
                                        value={value}
                                        name={name}
                                        onChange={(e) => {
                                          const updatedTime = [...time];
                                          updatedTime[index] = {
                                            ...updatedTime[index],
                                            end: e.value,
                                          };
                                          setValue(
                                            `callDays.${day}.time`,
                                            updatedTime
                                          );
                                          onChange(e);
                                        }}
                                      />
                                    );
                                  }}
                                />
                              </div>
                              {index === 0 ? (
                                <Button
                                  className="cursor-pointer p-0 ml-2 !bg-transparent"
                                  onClick={() => {
                                    const updatedTime = [
                                      ...time,
                                      {
                                        start: "",
                                        end: "",
                                        id: Date.now() + Math.random(),
                                      },
                                    ];
                                    setValue(
                                      `callDays.${day}.time`,
                                      updatedTime
                                    );
                                  }}
                                >
                                  <PlusIcon className={"text-primary-dark"} />
                                </Button>
                              ) : (
                                <Button
                                  className="cursor-pointer p-0 ml-2 !bg-transparent"
                                  onClick={() => {
                                    const updatedTime = time.filter(
                                      (item) => item.id !== slot.id
                                    );
                                    setValue(
                                      `callDays.${day}.time`,
                                      updatedTime
                                    );
                                  }}
                                >
                                  <BinIcon className={"text-primary-dark"} />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {dayError && (
            <Text className="text-[red] font-switzer text-[12px] font-[400]">
              {dayError}
            </Text>
          )}
          <Text className="text-[#758599] font-switzer text-[12px] font-[400]">
            You can always adjust your availability in the{" "}
            <span className="text-primary-dark font-[500]">“Appointments”</span>{" "}
            menu
          </Text>
        </div>
        <Button
          type="submit"
          className="w-full h-[40px] !bg-btn"
          isLoading={loading}
        >
          <Text className="font-[500] font-switzer text-[12px] text-background">
            Set Availability
          </Text>
        </Button>
      </form>
    </div>
  );
}
